import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchHotLeagues,
  fetchAllLeagues,
  setLeagueResult,
} from '@/lib/redux/actions/leagueAction';
import { HOT_LEAGUES } from '@/constants';

/**
 * Custom hook to manage league state and operations
 * @returns {Object} League state and operations
 */
export const useLeague = () => {
  const dispatch = useDispatch();
  
  const {
    league: selectedLeague,
    hotLeagues,
    allLeagues,
    hotLeaguesLoading,
    allLeaguesLoading,
    hotLeaguesError,
    allLeaguesError,
  } = useSelector((state) => state.leagueReducer);

  /**
   * Select a league
   * @param {Object} league - League object to select
   */
  const selectLeague = useCallback(
    (league) => {
      if (league) {
        dispatch(setLeagueResult(league));
      }
    },
    [dispatch]
  );

  /**
   * Fetch hot leagues from API
   */
  const loadHotLeagues = useCallback(
    (params) => {
      dispatch(fetchHotLeagues(params));
    },
    [dispatch]
  );

  /**
   * Fetch all leagues from API
   */
  const loadAllLeagues = useCallback(
    (params) => {
      dispatch(fetchAllLeagues(params));
    },
    [dispatch]
  );

  /**
   * Get leagues with fallback to constants
   */
  const getLeaguesWithFallback = useCallback(() => {
    return hotLeagues && hotLeagues.length > 0 ? hotLeagues : HOT_LEAGUES;
  }, [hotLeagues]);

  useEffect(() => {
    if (!hotLeagues || hotLeagues.length === 0) {
      loadHotLeagues();
    }
  }, []);

  return {
    selectedLeague,
    hotLeagues: getLeaguesWithFallback(),
    allLeagues,
    isLoadingHot: hotLeaguesLoading,
    isLoadingAll: allLeaguesLoading,
    errorHot: hotLeaguesError,
    errorAll: allLeaguesError,

    selectLeague,
    loadHotLeagues,
    loadAllLeagues,
  };
};
